import { authService } from '../../features/auth/services/auth.service';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Track if we're currently refreshing to avoid multiple simultaneous refresh calls
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
  skipRetry?: boolean;
  timeout?: number; // Timeout in milliseconds (default: 60000 for 60 seconds, 180000 for 3 minutes)
}

/**
 * Makes an authenticated API request with automatic token refresh
 * @param url - The API endpoint (can be relative or absolute)
 * @param options - Fetch options with additional skipAuth and skipRetry flags
 * @returns The fetch response
 */
export async function apiClient(
  url: string,
  options: ApiRequestOptions = {}
): Promise<Response> {
  const { skipAuth = false, skipRetry = false, timeout, ...fetchOptions } = options;

  // Make URL absolute if it's relative
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  // Determine timeout: use provided timeout, or default to 60 seconds (60000ms)
  // For long-running requests like enrichment start and responses, use 3 minutes (180000ms)
  const defaultTimeout = 60000; // 60 seconds
  const longRunningTimeout = 180000; // 3 minutes

  // /enrichment/start is long-running (synchronous doc extraction before Celery dispatch).
  // /responses is now async (returns immediately after dispatch) so it no longer needs this.
  const isLongRunningEndpoint = url.includes('/enrichment/start');

  const requestTimeout = timeout || (isLongRunningEndpoint ? longRunningTimeout : defaultTimeout);

  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

  // If not skipping auth, add authorization header
  if (!skipAuth) {
    const token = authService.getApiToken();
    const isFormData = fetchOptions.body instanceof FormData;
    fetchOptions.headers = {
      // Don't set Content-Type for FormData — the browser sets it with the correct multipart boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...fetchOptions.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  // Add abort signal to fetch options
  fetchOptions.signal = controller.signal;

  try {
    // Make the request
    let response = await fetch(fullUrl, fetchOptions);
    clearTimeout(timeoutId);
    console.log(`[API Client] ${fetchOptions.method || 'GET'} ${url} - Status: ${response.status}`);

    // Handle 401/403 errors with token refresh
    if (!skipRetry && (response.status === 401 || response.status === 403)) {
      console.log('[API Client] ⚠️ Received 401/403 error');
      console.log('[API Client] URL:', fullUrl);
      console.log('[API Client] Has refresh token:', !!sessionStorage.getItem('refreshToken'));
      console.log('[API Client] Has ID token:', !!sessionStorage.getItem('idToken'));

      // Check if refresh token exists before attempting refresh
      const refreshToken = sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.error('[API Client] ❌ No refresh token available - NOT redirecting, just returning 401');
        // DON'T redirect - let the app handle it
        return response;
      }

      try {
        console.log('[API Client] 🔄 Attempting token refresh...');
        await refreshTokenIfNeeded();
        console.log('[API Client] ✅ Token refresh completed');

        // Retry the request with new token
        const newToken = authService.getApiToken();
        if (!newToken) {
          console.error('[API Client] ❌ No token after refresh - NOT redirecting');
          return response;
        }

        // Create new controller for retry
        const retryController = new AbortController();
        const retryTimeoutId = setTimeout(() => retryController.abort(), requestTimeout);

        const retryFetchOptions = {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            Authorization: `Bearer ${newToken}`,
          },
          signal: retryController.signal,
          skipRetry: true,
        } as ApiRequestOptions;

        console.log('[API Client] 🔄 Retrying original request with new token...');
        response = await fetch(fullUrl, retryFetchOptions);
        clearTimeout(retryTimeoutId);
        console.log('[API Client] Retry response status:', response.status);

        // If still unauthorized after refresh, check for premium subscription error
        if (response.status === 403) {
          try {
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json();
            const errorMessage = errorData.detail || errorData.message || errorData.error || '';
            if (
              typeof errorMessage === 'string' &&
              errorMessage.includes('Premium subscription required')
            ) {
              // Dispatch custom event to trigger upgrade modal
              window.dispatchEvent(new CustomEvent('showUpgradeModal'));
              // Return a modified response that won't trigger error toasts
              return new Response(JSON.stringify({ ...errorData, _premiumError: true }), {
                status: 403,
                statusText: response.statusText,
                headers: response.headers,
              });
            }
          } catch (e) {
            // If parsing fails, continue with normal error handling
          }
        }
        if (response.status === 401 || response.status === 403) {
          console.error('[API Client] ❌ Still unauthorized after token refresh - NOT redirecting');
          // Just return the response, let the app decide what to do
        }
      } catch (error) {
        console.error('[API Client] ❌ Token refresh error:', error);
        // DON'T redirect on error - just return the failed response
        return response;
      }
    }

    // Check for premium subscription error on 403 responses (even if not 401/403 initially)
    if (response.status === 403 && !skipRetry) {
      try {
        const clonedResponse = response.clone();
        const errorData = await clonedResponse.json();
        const errorMessage = errorData.detail || errorData.message || errorData.error || '';
        if (
          typeof errorMessage === 'string' &&
          errorMessage.includes('Premium subscription required')
        ) {
          // Dispatch custom event to trigger upgrade modal
          window.dispatchEvent(new CustomEvent('showUpgradeModal'));
          // Return a modified response that won't trigger error toasts
          return new Response(JSON.stringify({ ...errorData, _premiumError: true }), {
            status: 403,
            statusText: response.statusText,
            headers: response.headers,
          });
        }
      } catch (e) {
        // If parsing fails, continue with normal error handling
      }
    }

    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);

    // Handle timeout/abort errors
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      console.error(`[API Client] ⏱️ Request timeout after ${requestTimeout}ms: ${url}`);
      throw new Error(`Request timeout: The request took longer than ${requestTimeout / 1000} seconds to complete.`);
    }

    // Re-throw other errors
    throw error;
  }
}

/**
 * Refreshes the token if needed, ensuring only one refresh happens at a time
 */
async function refreshTokenIfNeeded(): Promise<void> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = authService.refreshToken()
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
}

// DISABLED - Let the app handle redirects
// This function is currently not being called

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (url: string, options?: ApiRequestOptions) =>
    apiClient(url, { ...options, method: 'GET' }),

  post: (url: string, data?: any, options?: ApiRequestOptions) =>
    apiClient(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: (url: string, data?: any, options?: ApiRequestOptions) =>
    apiClient(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: (url: string, data?: any, options?: ApiRequestOptions) =>
    apiClient(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: (url: string, options?: ApiRequestOptions) =>
    apiClient(url, { ...options, method: 'DELETE' }),
};

